import type { ReviewAnswers, ReviewStatementDraft } from "../domain/review";
import type { ReviewIssueType } from "../domain/types";
import { RuleErrorCode, type ValidationIssue } from "./errors";

export interface ReviewSynthesisResult {
  status: "ready" | "insufficient_data";
  issueType: ReviewIssueType;
  issues: ValidationIssue[];
  founderDeviation: string;
  affectedBusinessResult: string;
  correctionAction: string;
  verificationMetric: string;
  statements: ReviewStatementDraft[];
  exportFieldDrafts: {
    uncomfortable_truth: string | null;
    focus_deviation: string | null;
    repetition_failure: string | null;
    trust_deviation: string | null;
    decision_quality_issue: string | null;
    delegation_issue: string | null;
    risk_issue: string | null;
    energy_issue: string | null;
    field_adaptation_issue: string | null;
  };
}

const ACTION_VERBS =
  /完成|联系|提出|发送|记录|访谈|交付|拒绝|停止|提交|确认|跟进|删除|召开|输出|询问|演示|测试|签署|回复/;
const ABSTRACT_CORRECTIONS =
  /^(更努力|保持专注|提高执行力|提升执行力|更加自律|提升自己|改善状态|做好时间管理)[。.!！]?$/;

function requiredIssue(
  condition: boolean,
  code: ValidationIssue["code"],
  message: string,
  path: string,
): ValidationIssue[] {
  return condition ? [] : [{ code, message, path }];
}

export function validateConcreteCorrection(
  action: string,
  verificationMetric: string,
): ValidationIssue[] {
  const normalized = action.trim();
  return [
    ...requiredIssue(
      normalized.length >= 8,
      RuleErrorCode.CORRECTION_ACTION_REQUIRED,
      "明日行为纠偏必须是一项可以执行的具体动作。",
      "answers.correctionAction",
    ),
    ...requiredIssue(
      normalized.length >= 8 &&
        !ABSTRACT_CORRECTIONS.test(normalized) &&
        ACTION_VERBS.test(normalized),
      RuleErrorCode.CORRECTION_ACTION_TOO_ABSTRACT,
      "请使用可观察动作，例如联系、提出、完成或记录；不要只写“更努力”或“保持专注”。",
      "answers.correctionAction",
    ),
    ...requiredIssue(
      verificationMetric.trim().length >= 4,
      RuleErrorCode.CORRECTION_VERIFICATION_REQUIRED,
      "请说明明天如何判断该动作是否完成。",
      "answers.verificationMetric",
    ),
  ];
}

export function synthesizeReview(
  answers: ReviewAnswers,
  options: {
    existingStatements?: ReviewStatementDraft[];
    idFactory?: () => string;
  } = {},
): ReviewSynthesisResult {
  const issues: ValidationIssue[] = [
    ...requiredIssue(
      answers.uncomfortableTruth.trim().length >= 4,
      RuleErrorCode.REVIEW_TRUTH_REQUIRED,
      "请记录今天最重要的不舒服事实；如果证据不足，可以明确写“数据不足”。",
      "answers.uncomfortableTruth",
    ),
    ...requiredIssue(
      answers.founderDeviation.trim().length >= 4,
      RuleErrorCode.REVIEW_DEVIATION_REQUIRED,
      "请只描述一个影响经营的个人行为偏差。",
      "answers.founderDeviation",
    ),
    ...requiredIssue(
      answers.affectedBusinessResult.trim().length >= 4,
      RuleErrorCode.REVIEW_BUSINESS_IMPACT_REQUIRED,
      "请说明该偏差影响了哪个业务结果。",
      "answers.affectedBusinessResult",
    ),
    ...validateConcreteCorrection(answers.correctionAction, answers.verificationMetric),
  ];
  const existing = new Map(
    (options.existingStatements ?? []).map((statement) => [statement.id, statement]),
  );
  const byContent = new Map(
    [...existing.values()].map((statement) => [statement.content, statement]),
  );
  const idFactory = options.idFactory ?? (() => crypto.randomUUID());
  const statement = (
    content: string,
    statementType: ReviewStatementDraft["statementType"],
  ): ReviewStatementDraft => {
    const prior = byContent.get(content);
    return (
      prior ?? {
        id: idFactory(),
        content,
        statementType,
        userConfirmation: "unconfirmed",
      }
    );
  };
  const statements = [
    ...(answers.uncomfortableTruth.trim()
      ? [statement(answers.uncomfortableTruth.trim(), "fact")]
      : []),
    ...(answers.founderDeviation.trim()
      ? [statement(answers.founderDeviation.trim(), "inference")]
      : []),
    ...(answers.correctionAction.trim()
      ? [statement(answers.correctionAction.trim(), "recommendation")]
      : []),
  ];
  const issueType: ReviewIssueType = answers.issueType || "insufficient_data";
  const ready = issues.length === 0 && Boolean(answers.issueType);

  return {
    status: ready ? "ready" : "insufficient_data",
    issueType: ready ? issueType : "insufficient_data",
    issues,
    founderDeviation: answers.founderDeviation.trim(),
    affectedBusinessResult: answers.affectedBusinessResult.trim(),
    correctionAction: answers.correctionAction.trim(),
    verificationMetric: answers.verificationMetric.trim(),
    statements,
    exportFieldDrafts: {
      uncomfortable_truth: answers.uncomfortableTruth.trim() || null,
      focus_deviation: answers.focus.trim() || null,
      repetition_failure: answers.repetition.trim() || null,
      trust_deviation: answers.trust.trim() || null,
      decision_quality_issue: answers.decisionQuality.trim() || null,
      delegation_issue: answers.organization.trim() || null,
      risk_issue: null,
      energy_issue:
        answers.energyLevel === null
          ? null
          : `用户自评今日经营相关能量为 ${answers.energyLevel}/5。`,
      field_adaptation_issue: answers.fieldAnswer.trim() || null,
    },
  };
}
